// pages/compliance/ComplianceList.jsx
import { Card, Image, Flex, Spin, Button, Typography, message, Upload, Tag, Space, Popconfirm } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { complianceType } from "../../constant";
import { 
  CheckOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  UploadOutlined,
  EyeOutlined,
  FileOutlined,
  ReloadOutlined,
  DeleteOutlined
} from "@ant-design/icons";
import ReactApexChart from "react-apexcharts";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import LocationSelector from "../../components/LocationSelector";
import { 
  useFetchCompliancesWithSubmissionQuery,
  useGetAllDocumentationQuery,
  useUpsertDocumentationMutation,
  useDeleteDocumentationMutation
} from "./compliance.service";
import { BACKEND_URL } from "../../constant";
import "./style.css";

const { Title, Text } = Typography;

const piChartOptions = {
  chart: {
    width: 380,
    type: "pie",
  },
  colors: ["#4CAF50", "#F44336"],
  labels: ["Completed", "Not Completed"],
  responsive: [
    {
      breakpoint: 480,
      options: {
        chart: {
          width: 200,
        },
        legend: {
          position: "bottom",
        },
      },
    },
  ],
};

// ✅ Documentation Types
const documentationTypes = [
  {
    title: "Pest Control Certificate",
    value: "PEST_CONTROL_CERTIFICATE",
    icon: <FilePdfOutlined style={{ fontSize: 28, color: "#ff4d4f" }} />,
    description: "Upload your pest control certificate",
    acceptedFormats: "PDF, JPG, PNG, GIF, WebP",
  },
  {
    title: "Oil Removal Certificate",
    value: "OIL_CERTIFICATE_REMOVAL",
    icon: <FileImageOutlined style={{ fontSize: 28, color: "#1890ff" }} />,
    description: "Upload your oil removal certificate",
    acceptedFormats: "PDF, JPG, PNG, GIF, WebP",
  },
];

export default function ComplianceList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.app.user);

  const [locationId, setLocationId] = useState(null);
  const [state, setState] = useState([0, 100]);
  const [completedTypes, setCompletedTypes] = useState([]);
  
  // ✅ Documentation states
  const [uploading, setUploading] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    const locId = user?.activeLocationId || user?.restaurantId;
    if (locId) {
      setLocationId(locId);
    }
  }, [user]);

  const handleLocationChange = (newLocationId) => {
    setLocationId(newLocationId);
    setCompletedTypes([]);
    setState([0, 100]);
  };

  // ✅ Compliance Query
  const {
    data = { data: { compliances: [] } },
    isFetching,
    isSuccess,
  } = useFetchCompliancesWithSubmissionQuery(
    { type: undefined, locationId },
    {
      refetchOnMountOrArgChange: true,
      skip: !locationId,
    }
  );

  // ✅ Documentation Queries
  const {
    data: documentationData,
    isFetching: isDocFetching,
    refetch: refetchDocs,
  } = useGetAllDocumentationQuery(
    { locationId },
    {
      skip: !locationId,
    }
  );

  const [upsertDocumentation, { isLoading: isUpserting }] = useUpsertDocumentationMutation();
  const [deleteDocumentation, { isLoading: isDeletingDoc }] = useDeleteDocumentationMutation();

  const { compliances } = data.data;
  const documentationList = documentationData?.data?.documentation || [];

  // ✅ Compliance Progress Calculation
  useEffect(() => {
    if (isSuccess && compliances?.length > 0 && locationId) {
      console.log("📍 Calculating progress for location:", locationId);
      console.log("📋 Compliances count:", compliances.length);

      const typeMap = new Map();
      
      compliances.forEach((item) => {
        if (!item.isSection) {
          const type = item.type;
          if (!typeMap.has(type)) {
            typeMap.set(type, { total: 0, completed: 0 });
          }
          const stats = typeMap.get(type);
          stats.total += 1;
          const isCompleted = item.submissionData?.isCompleted || 
                             (item.submissionData?.comment?.default && 
                              item.submissionData?.comment?.default !== "");
          if (isCompleted) {
            stats.completed += 1;
          }
        }
      });

      const completed = [];
      typeMap.forEach((stats, type) => {
        if (stats.total > 0 && stats.completed === stats.total) {
          completed.push(type);
        }
      });

      setCompletedTypes(completed);

      const total = compliances.filter((item) => !item.isSection).length;
      const completedCount = compliances.filter(
        (item) => !item.isSection && 
        (item.submissionData?.isCompleted || 
         (item.submissionData?.comment?.default && 
          item.submissionData?.comment?.default !== ""))
      ).length;
      
      const percentage = total > 0 ? Number(((completedCount / total) * 100).toFixed(1)) : 0;
      
      console.log(`📊 Total: ${total}, Completed: ${completedCount}, Percentage: ${percentage}%`);
      
      setState([percentage, 100 - percentage]);
    } else if (isSuccess && locationId) {
      setState([0, 100]);
      setCompletedTypes([]);
    }
  }, [compliances, isSuccess, locationId]);

  const handleNavigation = (type) => {
    navigate(`/compliance_checks/checklist/${type}`);
  };

  const getTypeStatus = (typeValue) => {
    if (completedTypes.includes(typeValue)) {
      return "completed";
    }
    return "pending";
  };

  // ✅ Documentation Handlers
  const isDocumentUploaded = (type) => {
    return documentationList.some(
      (doc) => doc.type === type && doc.isUploaded
    );
  };

  const getDocumentInfo = (type) => {
    return documentationList.find((doc) => doc.type === type);
  };

  const handleFileUpload = async (file, type, title) => {
    setUploading(type);
    setUploadProgress((prev) => ({ ...prev, [type]: 0 }));

    try {
      const formData = new FormData();
      formData.append("type", type);
      formData.append("file", file);
      formData.append("title", title);

      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const baseName = file.name.replace(/\.[^/.]+$/, '');
      const newFileName = `${type}_${timestamp}_${baseName}.${fileExtension}`;
      formData.append("fileName", newFileName);

      setUploadProgress((prev) => ({ ...prev, [type]: 50 }));

      await upsertDocumentation({ data: formData, locationId }).unwrap();

      setUploadProgress((prev) => ({ ...prev, [type]: 100 }));
      message.success({
        content: `${title} uploaded successfully!`,
        duration: 3,
      });
      refetchDocs();
    } catch (error) {
      console.error("Upload error:", error);
      message.error({
        content: error?.data?.message || "Failed to upload document. Please try again.",
        duration: 4,
      });
    } finally {
      setUploading(null);
      setUploadProgress((prev) => ({ ...prev, [type]: 0 }));
    }
    return false;
  };

  const handleDeleteDocument = async (type, title) => {
    setDeleting(type);
    try {
      await deleteDocumentation({ type, locationId }).unwrap();
      message.success({
        content: `${title} deleted successfully!`,
        duration: 3,
      });
      refetchDocs();
    } catch (error) {
      console.error("Delete error:", error);
      message.error({
        content: error?.data?.message || "Failed to delete document. Please try again.",
        duration: 4,
      });
    } finally {
      setDeleting(null);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType === 'pdf') {
      return <FilePdfOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />;
    }
    if (fileType === 'image') {
      return <FileImageOutlined style={{ fontSize: 24, color: '#1890ff' }} />;
    }
    return <FileOutlined style={{ fontSize: 24, color: '#999' }} />;
  };

  const getFileTypeLabel = (fileType) => {
    if (fileType === 'pdf') return 'PDF';
    if (fileType === 'image') return 'Image';
    return 'File';
  };

  const uploadedCount = documentationList.filter((doc) => doc.isUploaded).length;

  return (
    <Spin spinning={isFetching}>
      <Card
        title={
          <Flex align="center" gap="middle" wrap>
            <span>{t("Compliance Checklist")}</span>
            <LocationSelector onLocationChange={handleLocationChange} />
          </Flex>
        }
        className="compliance-list-page"
        bordered={false}
      >
        {!locationId ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
            {t("Please select a location to view compliance checklist")}
          </div>
        ) : (
          <>
            {/* ✅ Compliance Sections Grid */}
            {complianceType.map((item, key) => {
              const status = getTypeStatus(item.value);
              return (
                <div
                  key={key}
                  className="compliance-list-page-item"
                  onClick={() => handleNavigation(item.value)}
                  style={{ cursor: "pointer" }}
                >
                  <img
                    width={200}
                    src={item.image_path}
                    alt="Compliance type"
                    style={{
                      objectFit: "cover",
                      border: "1px solid #d9d9d9",
                      borderRadius: "6px",
                    }}
                  />
                  <div className="compliance-list-page-item-title">
                    <h3>{t(item.title)}</h3>
                    <CheckOutlined
                      className={
                        status === "completed"
                          ? "checklist-complete"
                          : "checklist-pending"
                      }
                    />
                  </div>
                </div>
              );
            })}

            {/* ✅ Pie Chart */}
            <ReactApexChart
              className="compliance-pie-chart"
              options={piChartOptions}
              series={state}
              type="pie"
              width={380}
            />

            {/* ✅ Documentation Section */}
            <div className="documentation-section">
              <Flex align="center" justify="space-between" wrap>
                <div>
                  <Title level={4} className="documentation-title">
                    📄 Documentation
                  </Title>
                  <Text type="secondary" className="documentation-subtitle">
                    Upload your certificates and documents.
                  </Text>
                </div>
                <Tag color="blue" style={{ fontSize: 12 }}>
                  {uploadedCount}/{documentationTypes.length} Uploaded
                </Tag>
              </Flex>

              <Spin spinning={isDocFetching}>
                <div className="documentation-grid">
                  {documentationTypes.map((item) => {
                    const isUploaded = isDocumentUploaded(item.value);
                    const docInfo = getDocumentInfo(item.value);
                    const isUploading = uploading === item.value;
                    const isDeleting = deleting === item.value;

                    return (
                      <div
                        key={item.value}
                        className="documentation-item"
                        style={{
                          backgroundColor: isUploaded ? "#f6ffed" : "#fafafa",
                          border: isUploaded 
                            ? "2px solid #b7eb8f" 
                            : "1px dashed #d9d9d9",
                        }}
                      >
                        {/* Status Badge */}
                        <div style={{ position: "absolute", top: 12, right: 12 }}>
                          {isUploaded ? (
                            <Tag color="success" style={{ fontSize: 11 }}>✓ Uploaded</Tag>
                          ) : (
                            <Tag color="warning" style={{ fontSize: 11 }}>Pending</Tag>
                          )}
                        </div>

                        {/* Icon */}
                        <div className="documentation-item-icon">
                          {isUploaded && docInfo ? (
                            getFileIcon(docInfo.fileType)
                          ) : (
                            item.icon
                          )}
                        </div>

                        {/* Title */}
                        <div className="documentation-item-title">
                          {item.title}
                        </div>

                        {/* Description / File Info */}
                        <div className="documentation-item-desc">
                          {isUploaded && docInfo ? (
                            <Space direction="vertical" size={2}>
                              <Text ellipsis style={{ maxWidth: "200px", fontSize: 13 }}>
                                📎 {docInfo.fileName || 'Document'}
                              </Text>
                              <Tag color="blue" style={{ fontSize: 10 }}>
                                {getFileTypeLabel(docInfo.fileType)}
                              </Tag>
                            </Space>
                          ) : (
                            <span>{item.description}</span>
                          )}
                        </div>

                        {/* Supported formats */}
                        {!isUploaded && (
                          <div style={{ fontSize: 11, color: "#bbb", marginBottom: 12 }}>
                            Supports: {item.acceptedFormats} • Max 10MB
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="documentation-item-actions">
                          <Upload
                            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                            showUploadList={false}
                            beforeUpload={(file) => {
                              const isValidType =
                                file.type === 'application/pdf' ||
                                file.type.startsWith('image/');
                              if (!isValidType) {
                                message.error('Please upload PDF or image file');
                                return false;
                              }
                              const isLt10M = file.size / 1024 / 1024 < 10;
                              if (!isLt10M) {
                                message.error('File must be smaller than 10MB');
                                return false;
                              }
                              handleFileUpload(file, item.value, item.title);
                              return false;
                            }}
                          >
                            <Button
                              type="primary"
                              icon={isUploaded ? <ReloadOutlined /> : <UploadOutlined />}
                              loading={isUploading || isUpserting}
                              style={{
                                backgroundColor: isUploaded ? "#52c41a" : "#2F2E70",
                                borderColor: isUploaded ? "#52c41a" : "#2F2E70",
                                minWidth: "80px",
                              }}
                            >
                              {isUploaded ? "Replace" : "Upload"}
                            </Button>
                          </Upload>

                          {isUploaded && docInfo && (
                            <>
                              <Button
                                type="default"
                                icon={<EyeOutlined />}
                                onClick={() => {
                                  if (docInfo?.fileUrl) {
                                    window.open(
                                      `${BACKEND_URL}${docInfo.fileUrl}`,
                                      "_blank"
                                    );
                                  }
                                }}
                              >
                                View
                              </Button>

                              <Popconfirm
                                title="Delete Document"
                                description={`Are you sure you want to delete "${item.title}"?`}
                                onConfirm={() =>
                                  handleDeleteDocument(item.value, item.title)
                                }
                                okText="Yes, Delete"
                                cancelText="Cancel"
                                okButtonProps={{ danger: true }}
                              >
                                <Button
                                  danger
                                  icon={<DeleteOutlined />}
                                  loading={isDeleting || isDeletingDoc}
                                  style={{ minWidth: "60px" }}
                                >
                                  Delete
                                </Button>
                              </Popconfirm>
                            </>
                          )}
                        </div>

                        {/* Upload Progress */}
                        {isUploading && uploadProgress[item.value] > 0 && (
                          <div style={{ width: "100%", marginTop: 12 }}>
                            <div style={{
                              height: 4,
                              background: "#f0f0f0",
                              borderRadius: 2,
                              overflow: "hidden",
                            }}>
                              <div style={{
                                width: `${uploadProgress[item.value]}%`,
                                height: "100%",
                                background: "linear-gradient(90deg, #52c41a, #1890ff)",
                                borderRadius: 2,
                                transition: "width 0.3s",
                              }} />
                            </div>
                            <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                              Uploading... {uploadProgress[item.value]}%
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Spin>
            </div>
          </>
        )}
      </Card>
    </Spin>
  );
}